import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Image as KonvaImage, Layer, Stage, Transformer, Group} from 'react-konva';
import useImage from 'use-image';

import {Title} from '../../components/Title';
import {Subtitle} from '../../components/Subtitle';
import {useTranslation} from '../../hooks/useTranslation';
import {useEditor} from '../../providers/EditorContext';
import {Button} from '../../components/Button';
import {useNavigate} from 'react-router-dom';
import {FilePickerButton} from '../../components/FilePickerButton';
import {stageToFile} from '../../utils/canvas';
import watermarkSrc from '../../assets/images/watermark.png';

import styles from './styles.module.scss';
import {useAppImage} from '../../hooks/useAppImage';

type PhotoTransform = { x: number; y: number; scaleX: number; scaleY: number };

type Watermark = {
    id: string;
    x: number;
    y: number;
    scale: number;
    rotation: number;
    opacity: number;
};

const rand = (min: number, max: number) => Math.random() * (max - min) + min;

const makeDiagonalWatermarks = (
    stageW: number,
    stageH: number,
    opts?: {
        angleDeg?: number;      // e.g. -25
        stepX?: number;         // spacing along X
        stepY?: number;         // spacing along Y
        margin?: number;        // extra coverage outside stage
        scaleMin?: number;
        scaleMax?: number;
        opacityMin?: number;
        opacityMax?: number;
    },
): Watermark[] => {
    const angleDeg = opts?.angleDeg ?? -25;
    const stepX = opts?.stepX ?? 140;
    const stepY = opts?.stepY ?? 120;
    const margin = opts?.margin ?? 220;

    const scaleMin = opts?.scaleMin ?? 0.12;
    const scaleMax = opts?.scaleMax ?? 0.22;

    const opacityMin = opts?.opacityMin ?? 0.50;
    const opacityMax = opts?.opacityMax ?? 0.80;

    // we generate a grid bigger than stage, then rotate the whole pattern visually
    // easiest: place items on expanded box and just set rotation on each image
    const w = stageW + margin * 2;
    const h = stageH + margin * 2;

    const list: Watermark[] = [];
    let idx = 0;

    for (let y = -margin; y <= h - margin; y += stepY) {
        // slight row offset helps make it look more "natural"
        const rowOffset = ((y / stepY) % 2) * (stepX * 0.5);

        for (let x = -margin; x <= w - margin; x += stepX) {
            list.push({
                id: `${Date.now()}-${idx++}`,
                x: x + rowOffset,
                y,
                scale: rand(scaleMin, scaleMax),
                rotation: angleDeg,
                opacity: rand(opacityMin, opacityMax),
            });
        }
    }

    return list;
};

export const Editor: FC = () => {
    // =========================
    // refs / state
    // =========================
    const stageRef = useRef<any>(null);
    const photoRef = useRef<any>(null);
    const trRef = useRef<any>(null);

    const wrapperRef = useRef<HTMLDivElement>(null);
    const stageWrapperRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const watermarkGroupRef = useRef<any>(null);

    const lastPinchDist = useRef<number | null>(null);

    const didInitFitForPhotoUrl = useRef<string | null>(null);
    const shouldResizeImage = useRef<boolean>(false);
    const isInteractingRef = useRef(false);
    const didHydrateFromStore = useRef(false);

    const [availableSize, setAvailableSize] = useState({w: 0, h: 0});
    const [footerH, setFooterH] = useState(0);

    const {
        selectedTemplate,
        setPhotoFile,
        photoUrl,
        photoTransform,
        setPhotoTransform,
        setResultFile,
        setClearResultFile,
    } = useEditor();

    const [photo, setPhoto] = useState<PhotoTransform>(() => photoTransform ?? {
        x: 50,
        y: 50,
        scaleX: 0.5,
        scaleY: 0.5
    });
    const photoStateRef = useRef(photo);

    const [watermarkImg] = useImage(watermarkSrc, 'anonymous');
    const [watermarks, setWatermarks] = useState<Watermark[]>([]);

    const navigate = useNavigate();

    // =========================
    // custom hooks
    // =========================
    const translate = useTranslation();

    const [photoImg] = useImage(photoUrl ?? '', 'anonymous');

    const {getImageUrl} = useAppImage();
    const templateSrc = useMemo(() => {
        if (!selectedTemplate) return '';
        return getImageUrl(selectedTemplate);
    }, [getImageUrl, selectedTemplate]);

    const [templateImg] = useImage(templateSrc, 'anonymous');

    // =========================
    // values / memos
    // =========================
    const isTouch = useMemo(() => {
        if (typeof window === 'undefined') return false;
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }, []);

    const isTemplateLoading = !templateImg;
    const isPhotoLoading = !!photoUrl && !photoImg;

    // Compute the final Stage size to match the template aspect ratio and fit inside the available box.
    const stageFit = useMemo(() => {
        const maxW = availableSize.w;
        const maxH = availableSize.h;
        if (!templateImg || maxW <= 0 || maxH <= 0) return {w: 0, h: 0};

        const aspect = templateImg.width / templateImg.height;

        let w = maxW;
        let h = Math.floor(w / aspect);

        if (h > maxH) {
            h = maxH;
            w = Math.floor(h * aspect);
        }

        return {w, h};
    }, [templateImg, availableSize.w, availableSize.h]);

    // Since the Stage is exactly the fitted template size, the template is drawn at (0,0).
    const templateDraw = useMemo(() => {
        if (!templateImg) return null;
        if (stageFit.w <= 0 || stageFit.h <= 0) return null;
        return {x: 0, y: 0, w: stageFit.w, h: stageFit.h};
    }, [templateImg, stageFit.w, stageFit.h]);

    // =========================
    // functions (callbacks)
    // =========================
    const measureFooter = useCallback(() => {
        const rect = footerRef.current?.getBoundingClientRect();
        setFooterH(Math.floor(rect?.height ?? 0));
    }, []);

    const measureAvailable = useCallback(() => {
        const el = stageWrapperRef.current;
        if (!el) return {w: 0, h: 0};

        const w = Math.max(0, el.clientWidth - 1); // ✅ -1 avoids the “1px overflow” issue
        const h = Math.max(0, el.clientHeight - footerH);

        return {w, h};
    }, [footerH]);

    const onPickFile = useCallback(
        (file: File) => {
            if (!file) return;
            shouldResizeImage.current = true;
            setPhotoFile(file);
        },
        [setPhotoFile]
    );

    const onTouchStart = useCallback((e: any) => {
        const touches = e.evt.touches;
        if (touches.length === 2) {
            isInteractingRef.current = true;
            e.evt.preventDefault();

            const [t1, t2] = touches;
            lastPinchDist.current = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

            if (photoRef.current) photoRef.current.draggable(false);
        }
    }, []);

    const onTouchMove = useCallback(
        (e: any) => {
            const stage = e.target.getStage();
            const touches = e.evt.touches;
            if (!stage || !photoImg || touches.length < 2) return;

            e.evt.preventDefault();

            const [t1, t2] = touches;
            const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

            if (!lastPinchDist.current) {
                lastPinchDist.current = dist;
                return;
            }

            const scaleBy = dist / lastPinchDist.current;
            lastPinchDist.current = dist;

            const MIN_SCALE = 0.05;
            const MAX_SCALE = 20;

            setPhoto((p) => {
                const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, p.scaleX * scaleBy));
                return {...p, scaleX: nextScale, scaleY: nextScale};
            });
        },
        [photoImg]
    );

    const onTouchEnd = useCallback(() => {
        lastPinchDist.current = null;
        isInteractingRef.current = false;
        setPhotoTransform(photoStateRef.current);
    }, [setPhotoTransform]);

    const onDragStart = useCallback(() => {
        isInteractingRef.current = true;
    }, []);

    const onDragEnd = useCallback(
        (e: any) => {
            const next = {...photo, x: e.target.x(), y: e.target.y()};
            setPhoto(next);
            setPhotoTransform(next);
            isInteractingRef.current = false;
        },
        [photo, setPhotoTransform]
    );

    const onTransformEnd = useCallback(() => {
        const node = photoRef.current;
        if (!node) return;

        const next = {
            x: node.x(),
            y: node.y(),
            scaleX: node.scaleX(),
            scaleY: node.scaleY(),
        };

        setPhoto(next);
        setPhotoTransform(next);
        isInteractingRef.current = false;
    }, [setPhotoTransform]);

    const goBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const onContinue = useCallback(async () => {
        trRef.current?.hide();

        watermarkGroupRef.current?.hide();
        const fileNameSalt = `${Date.now()}-${(Math.random() * 1000).toFixed(0)}`;

        const file = await stageToFile(stageRef.current, {
            filename: `edited_photo-${fileNameSalt}.png`,
            pixelRatio: 2,
        });

        setClearResultFile(file);

        // edited version
        watermarkGroupRef.current?.show();
        const rawFile = await stageToFile(stageRef.current, {
            filename: `raw_edited_photo-${fileNameSalt}.png`,
            pixelRatio: 2,
        });

        setResultFile(rawFile);


        trRef.current?.show();

        navigate('/checkout');
    }, [navigate, setResultFile, setClearResultFile]);

    // =========================
    // useEffects
    // =========================

    // redirect if no template
    useEffect(() => {
        if (!selectedTemplate) navigate('/', {replace: true});
    }, [navigate, selectedTemplate]);

    // keep ref synced
    useEffect(() => {
        photoStateRef.current = photo;
    }, [photo]);

    // footer height observer
    useEffect(() => {
        measureFooter();

        const el = footerRef.current;
        if (!el) return;

        const ro = new ResizeObserver(() => measureFooter());
        ro.observe(el);

        return () => ro.disconnect();
    }, [measureFooter]);

    // stage wrapper observer (available box size)
    useEffect(() => {
        const el = stageWrapperRef.current;
        if (!el) return;

        const update = () => setAvailableSize(measureAvailable());

        const ro = new ResizeObserver(() => update());
        ro.observe(el);

        // initial measure after layout
        requestAnimationFrame(update);

        return () => ro.disconnect();
    }, [measureAvailable]);

    // attach transformer when desktop + ready
    useEffect(() => {
        if (isTouch) return;
        if (!photoImg) return;
        if (!templateImg) return;
        if (!trRef.current || !photoRef.current) return;

        trRef.current.nodes([photoRef.current]);
        trRef.current.getLayer()?.batchDraw();
    }, [isTouch, photoImg, templateImg, stageFit.w, stageFit.h]);

    // hydrate from store once
    useEffect(() => {
        if (!photoTransform) return;
        if (didHydrateFromStore.current) return;
        didHydrateFromStore.current = true;
        setPhoto(photoTransform);
    }, [photoTransform]);

    // initial fit uploaded photo ONCE per uploaded photoUrl, inside templateDraw
    useEffect(() => {
        if (!photoImg || !photoUrl) return;
        if (stageFit.w <= 0 || stageFit.h <= 0) return;
        if (!shouldResizeImage.current) return;

        if (didInitFitForPhotoUrl.current === photoUrl) return;
        didInitFitForPhotoUrl.current = photoUrl;

        const padding = 20;

        const maxW = Math.max(1, stageFit.w - padding * 2);
        const maxH = Math.max(1, stageFit.h - padding * 2);

        const imgW = photoImg.width;
        const imgH = photoImg.height;

        const scale = Math.min(maxW / imgW, maxH / imgH, 1);

        const x = (stageFit.w - imgW * scale) / 2;
        const y = (stageFit.h - imgH * scale) / 2;

        const next = {x, y, scaleX: scale, scaleY: scale};
        setPhoto(next);
        setPhotoTransform(next);

        shouldResizeImage.current = false;
    }, [photoImg, photoUrl, stageFit.w, stageFit.h, setPhotoTransform]);

    // debounce saves (don’t spam context while still interacting)
    useEffect(() => {
        if (isInteractingRef.current) return;

        const id = window.setTimeout(() => {
            setPhotoTransform(photo);
        }, 150);

        return () => window.clearTimeout(id);
    }, [photo, setPhotoTransform]);

    useEffect(() => {
        if (!watermarkImg) return;
        if (stageFit.w <= 0 || stageFit.h <= 0) return;

        // regenerate when stage size changes (template change / resize)
        setWatermarks(
            makeDiagonalWatermarks(stageFit.w, stageFit.h, {
                angleDeg: -25,
                stepX: 160,
                stepY: 140,
                margin: 260,
                opacityMin: 0.18,
                opacityMax: 0.30,
                scaleMin: 0.12,
                scaleMax: 0.20,
            }),
        );
    }, [watermarkImg, stageFit.w, stageFit.h]);

    // =========================
    // render
    // =========================
    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div className="header">
                <Title text={translate('editorTitle')}/>
                <Subtitle text={translate('editorSubtitle')}/>
            </div>

            <div ref={stageWrapperRef} className={styles.stageWrapper}>
                {isTemplateLoading || stageFit.w === 0 || stageFit.h === 0 ? (
                    <div className={styles.loader} aria-busy="true">
                        <span className={styles.spinner}/>
                    </div>
                ) : (
                    <>
                        {isPhotoLoading && (
                            <div className={styles.overlayLoader} aria-hidden="true">
                                <span className={styles.spinner}/>
                            </div>
                        )}

                        <Stage
                            ref={stageRef}
                            width={stageFit.w}
                            height={stageFit.h}
                            onTouchStart={isTouch ? onTouchStart : undefined}
                            onTouchMove={isTouch ? onTouchMove : undefined}
                            onTouchEnd={isTouch ? onTouchEnd : undefined}
                            className={styles.stageContainer}
                        >
                            {/* PHOTO layer (clipped to template) */}
                            <Layer
                                clipFunc={(ctx) => {
                                    if (!templateDraw) return;
                                    ctx.rect(0, 0, stageFit.w, stageFit.h);
                                }}
                            >
                                {photoImg && templateDraw && (
                                    <KonvaImage
                                        ref={photoRef}
                                        image={photoImg}
                                        x={photo.x}
                                        y={photo.y}
                                        scaleX={photo.scaleX}
                                        scaleY={photo.scaleY}
                                        draggable={!isTouch || !lastPinchDist.current}
                                        onDragStart={onDragStart}
                                        onDragEnd={onDragEnd}
                                        onTransformEnd={onTransformEnd}
                                    />
                                )}
                            </Layer>

                            {/* OVERLAY layer (template + transformer) */}
                            <Layer>
                                {templateImg && templateDraw && (
                                    <KonvaImage
                                        image={templateImg}
                                        x={templateDraw.x}
                                        y={templateDraw.y}
                                        width={templateDraw.w}
                                        height={templateDraw.h}
                                        listening={false}
                                    />
                                )}

                                <Group ref={watermarkGroupRef} listening={false}>
                                    {watermarkImg &&
                                        watermarks.map((wm) => (
                                            <KonvaImage
                                                key={wm.id}
                                                image={watermarkImg}
                                                x={wm.x}
                                                y={wm.y}
                                                offsetX={watermarkImg.width / 2}
                                                offsetY={watermarkImg.height / 2}
                                                scaleX={wm.scale}
                                                scaleY={wm.scale}
                                                rotation={wm.rotation}
                                                opacity={wm.opacity}
                                                listening={false}
                                            />
                                        ))}
                                </Group>

                                {!isTouch && (
                                    <Transformer
                                        ref={trRef}
                                        rotateEnabled
                                        rotateLineVisible
                                        keepRatio
                                        boundBoxFunc={(oldBox: any, newBox: any) => {
                                            if (newBox.width < 20 || newBox.height < 20) return oldBox;
                                            return newBox;
                                        }}
                                    />
                                )}
                            </Layer>
                        </Stage>
                    </>
                )}
            </div>

            {/* IMPORTANT: attach ref so we can measure fixed footer height on mobile */}
            <div className="footer" ref={footerRef}>
                <div className="footer-content">
                    <Button className={styles.backButton} onClick={goBack} text={translate('goBack')}/>
                    <div className="btn-wrapper">
                        <FilePickerButton text={translate('filePicker')} onPickFile={onPickFile}/>
                        <Button text={translate('continue')} disabled={!photoImg || !templateImg} onClick={onContinue}/>
                    </div>
                </div>
            </div>
        </div>
    );
};
